import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
const METRO_LOGO_URL = 'https://cdn.magicpatterns.com/uploads/83gAX3TsGvnbvH5ew774Zt/Metro_Logo_HQ26.png';
export function ApplicationSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="application" ref={ref} className="min-h-screen px-8 py-24" data-id="element-31">
      <motion.div initial={{
      opacity: 0,
      y: 40
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="max-w-7xl mx-auto" data-id="element-32">
        <div className="mb-16" data-id="element-33">
          <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-4" data-id="element-34">
            07 / APPLICATION
          </div>
          <h2 className="font-space text-5xl md:text-6xl font-medium tracking-tight" data-id="element-35">
            System in Practice
          </h2>
          <p className="text-lg text-[var(--text-muted)] mt-4 max-w-2xl font-helvetica" data-id="element-36">
            See how the system reassembles into real surfaces — wordmark in
            context, controls, inputs, cards, and personality marks.
          </p>
        </div>

        <div className="space-y-12" data-id="element-37">
          <div data-id="element-38">
            <div className="font-mono text-xs text-[var(--text-muted)] mb-6" data-id="element-39">
              WORDMARK IN CONTEXT
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-40">
              <div className="bg-[var(--bg-canvas)] rounded-sm border border-[var(--divider)] p-10 flex items-center justify-center min-h-[160px]" data-id="element-41">
                <img src={METRO_LOGO_URL} alt="me—tro" className="w-full max-w-[160px] h-auto" data-id="element-42" />
              </div>
              <div className="bg-white rounded-sm border border-[var(--divider)] p-10 flex items-center justify-center min-h-[160px]" data-id="element-43">
                <img src={METRO_LOGO_URL} alt="me—tro" className="w-full max-w-[160px] h-auto" data-id="element-44" />
              </div>
              <div className="bg-white rounded-sm border border-[var(--divider)] p-10 flex items-center justify-center min-h-[160px]" data-id="element-45">
                <img src={METRO_LOGO_URL} alt="me—tro" className="w-full max-w-[160px] h-auto" style={{
                filter: 'brightness(0)'
              }} data-id="element-46" />
              </div>
              <div className="bg-[var(--text-ink)] rounded-sm border border-[var(--divider)] p-10 flex items-center justify-center min-h-[160px]" data-id="element-47">
                <img src={METRO_LOGO_URL} alt="me—tro" className="w-full max-w-[160px] h-auto" style={{
                filter: 'brightness(0) invert(1)'
              }} data-id="element-48" />
              </div>
            </div>
          </div>

          <div data-id="element-49">
            <div className="font-mono text-xs text-[var(--text-muted)] mb-6" data-id="element-50">
              BUTTONS
            </div>
            <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-12" data-id="element-51">
              <div className="flex flex-wrap items-center gap-4" data-id="element-52">
                <button className="px-7 py-3 bg-[var(--accent-blue)] text-white rounded-full font-helvetica text-sm font-medium hover:bg-opacity-90 transition-all flex items-center gap-2" data-id="element-53">
                  Start a project
                  <ArrowRightIcon className="w-4 h-4" strokeWidth={1.5} data-id="element-54" />
                </button>
                <button className="px-7 py-3 border border-[var(--accent-blue)] text-[var(--accent-blue)] rounded-full font-helvetica text-sm font-medium hover:bg-[var(--accent-blue)] hover:text-white transition-all" data-id="element-55">
                  See services
                </button>
                <button className="px-7 py-3 bg-[var(--accent-orange)] text-white rounded-full font-helvetica text-sm font-medium hover:bg-opacity-90 transition-all" data-id="element-56">
                  Talk to the team
                </button>
                <button className="px-7 py-3 text-[var(--text-ink)] rounded-full font-helvetica text-sm font-medium hover:bg-black hover:bg-opacity-5 transition-all" data-id="element-57">
                  Learn more
                </button>
              </div>
            </div>
          </div>

          <div data-id="element-58">
            <div className="font-mono text-xs text-[var(--text-muted)] mb-6" data-id="element-59">
              FORM INPUTS
            </div>
            <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-12" data-id="element-60">
              <div className="max-w-md space-y-6" data-id="element-61">
                <div data-id="element-62">
                  <label className="block text-sm font-helvetica font-medium mb-2" data-id="element-63">
                    Email Address
                  </label>
                  <input type="email" placeholder="you@example.com" className="w-full px-6 py-3 bg-[var(--bg-canvas)] border border-[var(--divider)] rounded-full font-helvetica focus:outline-none focus:border-[var(--accent-blue)] focus:ring-1 focus:ring-[var(--accent-blue)] transition-colors" data-id="element-64" />
                </div>
                <div data-id="element-65">
                  <label className="block text-sm font-helvetica font-medium mb-2" data-id="element-66">
                    Project brief
                  </label>
                  <textarea placeholder="Tell us what you're thinking…" rows={4} className="w-full px-6 py-4 bg-[var(--bg-canvas)] border border-[var(--divider)] rounded-lg font-helvetica focus:outline-none focus:border-[var(--accent-blue)] focus:ring-1 focus:ring-[var(--accent-blue)] transition-colors resize-none" data-id="element-67" />
                </div>
              </div>
            </div>
          </div>

          <div data-id="element-68">
            <div className="font-mono text-xs text-[var(--text-muted)] mb-6" data-id="element-69">
              SERVICE CARD
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-70">
              {[{
              title: 'Video & Photography',
              desc: 'Story-led visuals. Concept, production, and post under one roof.'
            }, {
              title: 'Marketing 360',
              desc: 'Strategy, content, and performance — measured against the metric that matters.'
            }].map((card, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.4,
              delay: i * 0.1
            }} className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-8 hover:border-[var(--text-ink)] transition-all group cursor-pointer" data-id="element-71">
                  <div className="w-14 h-14 rounded-full bg-[var(--accent-blue)] bg-opacity-10 flex items-center justify-center mb-6" data-id="element-72">
                    <svg viewBox="0 0 100 100" className="w-8 h-8" data-id="element-73">
                      <path d="M 15 50 L 50 50 L 85 15" fill="none" stroke="var(--accent-blue)" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" data-id="element-74" />
                      <circle cx="50" cy="50" r="7" fill="var(--bg-surface)" stroke="var(--accent-blue)" strokeWidth="4" data-id="element-75" />
                    </svg>
                  </div>
                  <h3 className="font-space text-2xl font-medium mb-3" data-id="element-76">
                    {card.title}
                  </h3>
                  <p className="text-[var(--text-muted)] font-helvetica leading-relaxed mb-6" data-id="element-77">
                    {card.desc}
                  </p>
                  <div className="flex items-center gap-2 text-sm font-medium font-helvetica text-[var(--accent-blue)] group-hover:gap-3 transition-all" data-id="element-78">
                    Learn more
                    <ArrowRightIcon className="w-4 h-4" strokeWidth={1.5} data-id="element-79" />
                  </div>
                </motion.div>)}
            </div>
          </div>

          <div data-id="element-80">
            <div className="font-mono text-xs text-[var(--text-muted)] mb-6" data-id="element-81">
              PERSONALITY MARKS
            </div>
            <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-12" data-id="element-82">
              <div className="flex flex-wrap justify-center gap-12" data-id="element-83">
                {/* Touchpoint sticker */}
                <div className="relative w-40 h-40 rounded-full bg-[var(--accent-blue)] flex items-center justify-center" data-id="element-84">
                  <svg viewBox="0 0 120 120" className="w-24 h-24 text-white" data-id="element-85">
                    <circle cx="60" cy="60" r="50" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.4" data-id="element-86" />
                    <circle cx="60" cy="60" r="34" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.7" data-id="element-87" />
                    <circle cx="60" cy="60" r="18" fill="currentColor" data-id="element-88" />
                  </svg>
                  <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full animate-[spin_18s_linear_infinite]" data-id="element-89">
                    <path id="textPath1" d="M 50 8 A 42 42 0 1 1 49.9 8" fill="none" data-id="element-90" />
                    <text className="text-[8px]" fill="var(--accent-amber)" style={{
                    fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
                    letterSpacing: '0.15em'
                  }} data-id="element-91">
                      <textPath href="#textPath1" startOffset="0%" data-id="element-92">
                        METRO · METRO · METRO · METRO · METRO ·{' '}
                      </textPath>
                    </text>
                  </svg>
                </div>

                {/* Cross-section sticker */}
                <div className="relative w-40 h-40 rounded-full bg-[var(--accent-green)] flex items-center justify-center" data-id="element-93">
                  <svg viewBox="0 0 120 120" className="w-24 h-24" data-id="element-94">
                    <line x1="20" y1="60" x2="100" y2="60" stroke="var(--bg-canvas)" strokeWidth="3" data-id="element-95" />
                    <line x1="60" y1="20" x2="60" y2="100" stroke="var(--bg-canvas)" strokeWidth="3" data-id="element-96" />
                    <circle cx="60" cy="60" r="8" fill="var(--accent-green)" stroke="var(--bg-canvas)" strokeWidth="3" data-id="element-97" />
                  </svg>
                  <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full animate-[spin_22s_linear_infinite_reverse]" data-id="element-98">
                    <path id="textPath2" d="M 50 12 A 38 38 0 1 1 49.9 12" fill="none" data-id="element-99" />
                    <text className="text-[8px]" fill="var(--bg-canvas)" style={{
                    fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
                    letterSpacing: '0.2em'
                  }} data-id="element-100">
                      <textPath href="#textPath2" startOffset="0%" data-id="element-101">
                        A NETWORK OF IDEAS · BARCELONA ·{' '}
                      </textPath>
                    </text>
                  </svg>
                </div>

                {/* Halo sticker */}
                <div className="relative w-40 h-40 rounded-full bg-[var(--bg-canvas)] border-2 border-[var(--accent-blue)] flex items-center justify-center" data-id="element-102">
                  <svg viewBox="0 0 120 120" className="w-28 h-28" data-id="element-103">
                    <circle cx="60" cy="60" r="50" fill="none" stroke="var(--accent-blue)" strokeWidth="1" opacity="0.3" data-id="element-104" />
                    <circle cx="60" cy="60" r="34" fill="none" stroke="var(--accent-blue)" strokeWidth="1" opacity="0.6" data-id="element-105" />
                    <circle cx="60" cy="60" r="18" fill="none" stroke="var(--accent-blue)" strokeWidth="2" data-id="element-106" />
                  </svg>
                </div>
              </div>
              <div className="mt-10 text-xs font-helvetica text-[var(--text-muted)] text-center max-w-md mx-auto" data-id="element-107">
                Geometric, system-friendly, scale-aware. Replaces the
                cartoon-hand era — same playful energy, expressed in 2026 form.
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>;
}